import React, { useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD = 8;

type Props = {
  onCasino: () => void;
  visible: boolean;
};

type Pos = { left: number; top: number };

const clampPos = (left: number, top: number, el: HTMLElement): Pos => {
  const img = el.querySelector("img");
  const width = Math.max(el.offsetWidth, img?.offsetWidth || 0);
  const height = Math.max(el.offsetHeight, img?.offsetHeight || 0);
  return {
    left: Math.min(Math.max(0, left), Math.max(0, window.innerWidth - width)),
    top: Math.min(Math.max(0, top), Math.max(0, window.innerHeight - height)),
  };
};

const DraggableGamesFab = ({ onCasino, visible }: Props) => {
  const elRef = useRef<HTMLAnchorElement>(null);
  const onCasinoRef = useRef(onCasino);
  const visibleRef = useRef(visible);
  const draggingRef = useRef(false);
  const dragRef = useRef({
    pointerId: -1,
    startX: 0,
    startY: 0,
    originLeft: 0,
    originTop: 0,
    moved: false,
    active: false,
  });
  const [pos, setPos] = useState<Pos | null>(null);
  const [dragging, setDragging] = useState(false);

  onCasinoRef.current = onCasino;
  visibleRef.current = visible;
  draggingRef.current = dragging;

  useEffect(() => {
    const onResize = () => {
      const el = elRef.current;
      if (!el) return;
      setPos((current) =>
        current ? clampPos(current.left, current.top, el) : current
      );
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  useEffect(() => {
    if (visible) return;
    dragRef.current.active = false;
    setDragging(false);
  }, [visible]);

  useEffect(() => {
    const finish = (e: PointerEvent, cancelled: boolean) => {
      const drag = dragRef.current;
      if (!drag.active || e.pointerId !== drag.pointerId) return;
      const moved = drag.moved;
      drag.active = false;
      drag.pointerId = -1;
      setDragging(false);
      try {
        elRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        // capture may already be released
      }
      if (!moved && !cancelled) onCasinoRef.current();
    };

    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.active || e.pointerId !== drag.pointerId) return;
      const el = elRef.current;
      if (!el) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      if (!drag.moved) {
        drag.moved = true;
        setDragging(true);
      }
      e.preventDefault();
      setPos(clampPos(drag.originLeft + dx, drag.originTop + dy, el));
    };

    const onUp = (e: PointerEvent) => finish(e, false);
    const onCancel = (e: PointerEvent) => finish(e, true);

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, []);

  const startDrag = (e: React.PointerEvent<HTMLAnchorElement>) => {
    if (!visibleRef.current) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const el = elRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originLeft: rect.left,
      originTop: rect.top,
      moved: false,
      active: true,
    };
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // window listeners still track the drag
    }
  };

  return (
    <a
      ref={elRef}
      className={`mobile-games-fab ui-link${dragging ? " is-dragging" : ""}${
        visible ? "" : " is-off-home"
      }`}
      style={
        pos
          ? { left: pos.left, top: pos.top, right: "auto", bottom: "auto" }
          : undefined
      }
      onPointerDown={startDrag}
      onClick={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      aria-label="Games"
    >
      <img
        src="/images/promot.svg"
        alt="Games"
        width={207}
        height={192}
        draggable={false}
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = "/images/promot.svg";
        }}
      />
    </a>
  );
};

export default DraggableGamesFab;
